import { Fragment, useMemo, useState } from "react";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import DataTable from "../../Components/DataTable";
import { getUserColumns } from "../../Components/user/user.columns";
import { useNavigate } from "react-router";
import { API_BASE_URL } from "../../api/api";
import { UserFilters } from "../../Components/user/UserFilters";
import UserStatsCards from "../../Components/user/UserStatsCards";
import { EditUserModal } from "../../Components/user/EditUserModal";
import { UserBonusModal } from "../../Components/user/UserBonusModal";
import { UserRangoDistribuidorModal } from "../../Components/user/UserRangoDistribuidorModal";
import Swal from "sweetalert2";
import UserRedAscendenteModal from "../../Components/user/UserRedAscendenteModal";
import { useAdmin } from "../../../context/AdminContext";
import { PageHeader } from "../../Components/common/PageHeader";

export const UserList = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const { getData, patchData, invalidateQuery } = useDataFetch();
  const { admin } = useAdmin();

  const { mutateAsync: updateUserNetwork } = patchData(
    `/users/update/userNetwork`,
    {},
    {
      onSuccess: (response) => {
        if (response?.message === "message_sent") {
          Swal.fire({
            icon: "success",
            title: "Red activada",
            text: `La red del usuario fue activada correctamente.`,
            timer: 2000,
            showConfirmButton: false,
          });
          invalidateQuery(["users"]);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No se pudo activar la red, intenta nuevamente.",
          });
        }
      },
      onError: (error) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Ocurrió un problema al activar la red.",
        });
      },
    }
  );

  const [filters, setFilters] = useState({
    name: "",
    document: "",
    code: "",
    sponsorCode: "",
    countryId: ""
  });

  const [activeFilters, setActiveFilters] = useState({
    name: "",
    document: "",
    code: "",
    sponsorCode: "",
    countryId: ""
  });

  const { data, isLoading } = getData(
    ["users", pagination, activeFilters],
    "/users/all/paginate",
    { enabled: true },
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...activeFilters,
    }
  );

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isBonusOpen, setIsBonusOpen] = useState(false);
  const [isRedAscendenteOpen, setIsRedAscendenteOpen] = useState(false);
  const [isRangoDistribuidorOpen, setIsRangoDistribuidorOpen] = useState(false);

  const toggleEdit = () => setIsEditOpen((prev) => !prev);
  const toggleBonus = () => setIsBonusOpen((prev) => !prev);
  const toggleRedAscendente = () => setIsRedAscendenteOpen((prev) => !prev);
  const toggleRangoDistribuidor = () => setIsRangoDistribuidorOpen((prev) => !prev);

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setIsEditOpen(true);
  };

  const handleBonusClick = (user) => {
    setSelectedUser(user);
    setIsBonusOpen(true);
  };

  const handleRedAscendenteClick = (user) => {
    setSelectedUser(user);
    setIsRedAscendenteOpen(true);
  };

  const handleRangoDistribuidorClick = (user) => {
    setSelectedUser(user);
    setIsRangoDistribuidorOpen(true);
  };

  const handleActivateNetwork = (user) => {
    Swal.fire({
      title: `Activar red usuario`,
      text: `¿Quieres activar la red del usuario: ${user.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4B36F0",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Sí, Activar`,
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        updateUserNetwork({ id: user.id })
      }
    });
  }

  const columns = useMemo(
    () => getUserColumns(
      navigate,
      handleEditClick,
      handleActivateNetwork,
      handleBonusClick,
      handleRedAscendenteClick,
      handleRangoDistribuidorClick,
      admin
    ),
    [navigate]
  );

  const users = data?.data?.users ?? [];
  const total = data?.data?.total ?? 0;

  const handleCreateUser = () => {
    navigate("/user/create");
  };

  const handleGenerateExcel = () => {
    const params = new URLSearchParams();

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    const url = `${API_BASE_URL}/users/report/excel?${params.toString()}`;
    window.open(url, "_blank");
  };

  const handleSearch = () => {
    setActiveFilters({ ...filters });
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <Fragment>
      <PageHeader
        title="Usuarios"
        crumbs={[{ label: "Red" }, { label: "Usuarios" }]}
        actions={
          <>
            <button type="button" className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1" onClick={handleGenerateExcel}>
              <i className="ri-file-excel-2-line" /> Excel
            </button>
            <button type="button" className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={handleCreateUser}>
              <i className="ri-user-add-line" /> Registrar cliente
            </button>
          </>
        }
      />

      <div className="mb-3">
        <UserStatsCards total={total} />
      </div>

      <DataTable
        data={users}
        total={total}
        pagination={pagination}
        setPagination={setPagination}
        columns={columns}
        progressPending={isLoading}
        minWidth={1560}
        emptyMessage="No hay usuarios para mostrar."
        filters={
          <UserFilters
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
          />
        }
      />

      {isEditOpen && (
        <EditUserModal
          isOpen={isEditOpen}
          toggle={toggleEdit}
          user={selectedUser}
        />
      )}

      {isBonusOpen && (
        <UserBonusModal
          isOpen={isBonusOpen}
          toggle={toggleBonus}
          user={selectedUser}
        />
      )}

      {isRedAscendenteOpen && (
        <UserRedAscendenteModal
          isOpen={isRedAscendenteOpen}
          toggle={toggleRedAscendente}
          user={selectedUser}
        />
      )}

      {isRangoDistribuidorOpen && (
        <UserRangoDistribuidorModal
          isOpen={isRangoDistribuidorOpen}
          toggle={toggleRangoDistribuidor}
          user={selectedUser}
        />
      )}
    </Fragment>
  );
};
